// src/components/ModalFormCliente.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import "animate.css";

import { putCliente as modifCliente } from "../helpers/rutaClientes";

/* ---------------- utils ---------------- */

const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toDateInputValue = (v) => {
  if (!v) return "";

  const d = new Date(v);

  if (Number.isNaN(d.getTime())) return "";

  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const clean = (v) => String(v ?? "").trim();

const emptyToNullDate = (v) => {
  const s = clean(v);
  return s || null;
};

const buildNombreCompleto = ({
  apellido1,
  apellido2,
  nombre,
  razonsocial,
}) => {
  const armado = [clean(apellido1), clean(apellido2), clean(nombre)]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(razonsocial);
};

const splitNombreLegacy = (cliente = {}) => {
  const apellido1 = clean(cliente?.apellido1);
  const apellido2 = clean(cliente?.apellido2);
  const nombre = clean(cliente?.nombre);

  if (apellido1 || apellido2 || nombre) {
    return { apellido1, apellido2, nombre };
  }

  const razon = clean(cliente?.razonsocial);

  if (!razon) {
    return {
      apellido1: "",
      apellido2: "",
      nombre: "",
    };
  }

  const partes = razon.split(/\s+/).filter(Boolean);

  return {
    apellido1: partes[0] || "",
    apellido2: partes[1] || "",
    nombre: partes.slice(2).join(" ") || "",
  };
};

const initial = {
  codcli: "",
  fecing: todayLocalISO(),

  // ID / Documento
  dni: "",

  // Nombre estilo USA / Cuba
  apellido1: "",
  apellido2: "",
  nombre: "",

  // Contacto
  telefono1: "",
  email: "",

  // Dirección USA
  domicilio: "",
  direccion2: "",
  ciudad: "",
  estado: "",
  zipCode: "",
  pais: "USA",

  // Compatibilidad visual con registros viejos
  razonsocial: "",
};

/* ---------------- Estados USA ---------------- */

const USA_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

export default function ModalFormCliente({
  cliente,
  handleClose,
  show,
  onSaved,
}) {
  const [formValues, setFormValues] = useState(initial);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ---------------- modal estado ---------------- */

  const [showEstadoModal, setShowEstadoModal] = useState(false);
  const [qEstado, setQEstado] = useState("");
  const [selIdxEstado, setSelIdxEstado] = useState(0);
  const inputEstadoRef = useRef(null);

  const estadoOpts = useMemo(() => {
    const q = clean(qEstado).toLowerCase();

    if (!q) return USA_STATES;

    return USA_STATES.filter((s) => {
      const code = s.code.toLowerCase();
      const name = s.name.toLowerCase();

      return code.includes(q) || name.includes(q);
    });
  }, [qEstado]);

  const closeEstadoModal = useCallback(() => {
    setShowEstadoModal(false);
  }, []);

  const openEstadoModal = useCallback(() => {
    setShowEstadoModal(true);
    setQEstado("");
    setSelIdxEstado(0);

    setTimeout(() => inputEstadoRef.current?.focus(), 50);
  }, []);

  const seleccionarEstado = useCallback(
    (estado) => {
      if (!estado) return;

      setFormValues((prev) => ({
        ...prev,
        estado: estado.code,
      }));

      closeEstadoModal();
    },
    [closeEstadoModal]
  );

  const onEstadoModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeEstadoModal();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setSelIdxEstado((i) =>
        Math.min(i + 1, Math.max(0, estadoOpts.length - 1))
      );

      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      setSelIdxEstado((i) => Math.max(i - 1, 0));

      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      const pick = estadoOpts[selIdxEstado] || estadoOpts[0];

      if (pick) seleccionarEstado(pick);
    }
  };

  /* ---------------- form helpers ---------------- */

  const setField = useCallback((name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const nombreCompleto = useMemo(
    () =>
      buildNombreCompleto({
        apellido1: formValues.apellido1,
        apellido2: formValues.apellido2,
        nombre: formValues.nombre,
        razonsocial: formValues.razonsocial,
      }),
    [
      formValues.apellido1,
      formValues.apellido2,
      formValues.nombre,
      formValues.razonsocial,
    ]
  );

  /* ---------------- hidratar cliente ---------------- */

  useEffect(() => {
    if (!show || !cliente) return;

    const split = splitNombreLegacy(cliente);

    const next = {
      codcli: clean(cliente?.codcli),
      fecing: toDateInputValue(cliente?.fecing) || todayLocalISO(),

      // ID / Documento
      dni: clean(cliente?.dni ?? cliente?.idDocumento ?? cliente?.documento),

      // Si vienen campos nuevos, los usa.
      // Si no, los arma desde razonsocial.
      apellido1: clean(cliente?.apellido1) || split.apellido1,
      apellido2: clean(cliente?.apellido2) || split.apellido2,
      nombre: clean(cliente?.nombre) || split.nombre,

      telefono1: clean(
        cliente?.telefono1 ??
          cliente?.telefono ??
          cliente?.mobile ??
          cliente?.celular1
      ),

      email: clean(cliente?.email),

      domicilio: clean(
        cliente?.domicilio ??
          cliente?.addressLine1 ??
          cliente?.direccion ??
          cliente?.AddressLine1
      ),

      direccion2: clean(
        cliente?.direccion2 ??
          cliente?.addressLine2 ??
          cliente?.AddressLine2
      ),

      ciudad: clean(
        cliente?.ciudad ?? cliente?.city ?? cliente?.Ciudad ?? cliente?.City
      ),

      estado: clean(
        cliente?.estado ?? cliente?.state ?? cliente?.Estado ?? cliente?.State
      ).toUpperCase(),

      zipCode: clean(
        cliente?.zipCode ??
          cliente?.zipcode ??
          cliente?.zip ??
          cliente?.ZIP ??
          cliente?.postalCode ??
          cliente?.codpostal
      ),

      pais: clean(cliente?.pais ?? cliente?.country ?? cliente?.Country) || "USA",

      razonsocial: clean(cliente?.razonsocial),
    };

    setFormValues(next);
    setQEstado("");
    setSelIdxEstado(0);
  }, [cliente, show]);

  /* ---------------- validate / payload ---------------- */

  const validate = () => {
    if (!clean(formValues.fecing)) {
      return "Ingresá la fecha de ingreso.";
    }

    if (!clean(formValues.dni)) {
      return "Ingresá el ID / número de documento.";
    }

    const nombreOk =
      clean(formValues.apellido1) ||
      clean(formValues.apellido2) ||
      clean(formValues.nombre) ||
      clean(formValues.razonsocial);

    if (!nombreOk) {
      return "Ingresá los datos del nombre del cliente.";
    }

    if (!clean(formValues.telefono1)) {
      return "Ingresá el teléfono.";
    }

    const email = clean(formValues.email);

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Ingresá un email válido.";
    }

    return "";
  };

  const buildPayload = () => {
    const apellido1 = clean(formValues.apellido1);
    const apellido2 = clean(formValues.apellido2);
    const nombre = clean(formValues.nombre);

    const razonsocial = buildNombreCompleto({
      apellido1,
      apellido2,
      nombre,
      razonsocial: formValues.razonsocial,
    });

    const payload = {
      codcli: clean(formValues.codcli),

      fecing: emptyToNullDate(formValues.fecing),

      // En pantalla dice ID, en backend usamos dni
      dni: clean(formValues.dni),

      apellido1,
      apellido2,
      nombre,

      // Compatibilidad con sistema anterior
      razonsocial,

      telefono1: clean(formValues.telefono1),
      email: clean(formValues.email),

      domicilio: clean(formValues.domicilio),
      direccion2: clean(formValues.direccion2),
      ciudad: clean(formValues.ciudad),
      estado: clean(formValues.estado).toUpperCase(),
      zipCode: clean(formValues.zipCode),
      pais: clean(formValues.pais) || "USA",

      activo: true,
    };

    if (!clean(payload.codcli)) {
      delete payload.codcli;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();

    if (msg) return alert(msg);

    const clienteId = cliente?._id || cliente?.id;

    if (!clienteId) {
      return alert("No se encontró el ID del cliente a modificar.");
    }

    const payload = buildPayload();
    console.log("📤 PAYLOAD MODAL CLIENTE:", payload);
    
    try {
      setSaving(true);

      const resp = await modifCliente(clienteId, payload);

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);

        if (typeof onSaved === "function") {
          onSaved(resp);
        }

        handleClose();
      }, 900);
    } catch (error) {
      console.error(
        "❌ Error al actualizar cliente:",
        error?.response?.data || error
      );

      const mensaje =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "No se pudo actualizar el cliente.";

      alert(mensaje);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        backdrop="static"
        scrollable
      >
        <form onSubmit={handleSubmit} noValidate>
          <Modal.Header closeButton>
            <Modal.Title>Modificar Cliente Comprador</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="container-fluid">
              {/* ---------------- fila 1 ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-1">
                  <label>Código</label>
                  <input
                    className="form-control"
                    name="codcli"
                    value={formValues.codcli}
                    disabled
                  />
                </div>

                <div className="form-group mt-2 col-md-2">
                  <label>Fecha Ingreso</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fecing"
                    value={formValues.fecing}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>ID / Documento</label>
                  <input
                    className="form-control"
                    name="dni"
                    value={formValues.dni}
                    onChange={handleChange}
                    placeholder="ID del cliente"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Teléfono</label>
                  <input
                    className="form-control"
                    name="telefono1"
                    value={formValues.telefono1}
                    onChange={handleChange}
                    placeholder="Mobile"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                    placeholder="cliente@email.com"
                  />
                </div>
              </div>

              {/* ---------------- fila 2 ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-4">
                  <label>Apellido 1</label>
                  <input
                    className="form-control"
                    name="apellido1"
                    value={formValues.apellido1}
                    onChange={handleChange}
                    placeholder="Primer apellido"
                  />
                </div>

                <div className="form-group mt-2 col-md-4">
                  <label>Apellido 2</label>
                  <input
                    className="form-control"
                    name="apellido2"
                    value={formValues.apellido2}
                    onChange={handleChange}
                    placeholder="Segundo apellido"
                  />
                </div>

                <div className="form-group mt-2 col-md-4">
                  <label>Nombre</label>
                  <input
                    className="form-control"
                    name="nombre"
                    value={formValues.nombre}
                    onChange={handleChange}
                    placeholder="Nombre"
                  />
                </div>
              </div>

              {/* ---------------- nombre completo ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-12">
                  <label>Nombre completo</label>
                  <input
                    className="form-control"
                    value={nombreCompleto}
                    readOnly
                    placeholder="Se arma automáticamente"
                  />
                  <small className="text-muted">
                    Se guarda también en <b>razonsocial</b> para mantener
                    compatibilidad con el sistema anterior.
                  </small>
                </div>
              </div>

              {/* ---------------- dirección ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-6">
                  <label>Domicilio / Address Line 1</label>
                  <input
                    className="form-control"
                    name="domicilio"
                    value={formValues.domicilio}
                    onChange={handleChange}
                    placeholder="Calle y número"
                  />
                </div>

                <div className="form-group mt-2 col-md-6">
                  <label>Apt / Suite / Unidad</label>
                  <input
                    className="form-control"
                    name="direccion2"
                    value={formValues.direccion2}
                    onChange={handleChange}
                    placeholder="Address Line 2 - Opcional"
                  />
                </div>
              </div>

              {/* ---------------- ciudad / estado / zip / país ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-3">
                  <label>Ciudad</label>
                  <input
                    className="form-control"
                    name="ciudad"
                    value={formValues.ciudad}
                    onChange={handleChange}
                    placeholder="City"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>Estado</label>

                  <div className="input-group">
                    <input
                      className="form-control"
                      name="estado"
                      value={formValues.estado}
                      onChange={(e) => {
                        setField("estado", e.target.value.toUpperCase());
                      }}
                      placeholder="Ej: FL"
                      maxLength={20}
                    />

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={openEstadoModal}
                      title="Buscar estado"
                      style={{
                        width: 44,
                        height: 38,
                        padding: 0,
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      🔎
                    </button>
                  </div>

                  <small className="text-muted">
                    Podés escribirlo o buscarlo.
                  </small>
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>ZIP Código</label>
                  <input
                    className="form-control"
                    name="zipCode"
                    value={formValues.zipCode}
                    onChange={handleChange}
                    placeholder="ZIP Code"
                  />
                </div>

                <div className="form-group mt-2 col-md-3">
                  <label>País</label>
                  <input
                    className="form-control"
                    name="pais"
                    value={formValues.pais}
                    onChange={handleChange}
                    placeholder="USA"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>

            <Button type="submit" variant="dark" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" /> Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ---------------- Modal buscar Estado ---------------- */}
      <Modal
        show={showEstadoModal}
        onHide={closeEstadoModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar estado</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Estado</Form.Label>
            <Form.Control
              ref={inputEstadoRef}
              value={qEstado}
              placeholder="Ej: FL o Florida"
              onChange={(e) => {
                setQEstado(e.target.value);
                setSelIdxEstado(0);
              }}
              onKeyDown={onEstadoModalKeyDown}
            />

            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!estadoOpts.length && (
              <div className="text-muted">Sin resultados.</div>
            )}

            {!!estadoOpts.length && (
              <ListGroup>
                {estadoOpts.map((estado, idx) => (
                  <ListGroup.Item
                    key={estado.code}
                    action
                    active={idx === selIdxEstado}
                    onClick={() => seleccionarEstado(estado)}
                  >
                    <b>{estado.code}</b> — {estado.name}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeEstadoModal}>
            Cerrar
          </Button>

          <Button
            variant="primary"
            onClick={() =>
              seleccionarEstado(estadoOpts[selIdxEstado] || estadoOpts[0])
            }
            disabled={!estadoOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------------- Modal éxito ---------------- */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Cambios guardados correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}